import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, useToggle } from '@factorialco/deprecated-design-system'
import { TransfersPageDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import useSearchParams from 'modules/core/hooks/useSearchParams'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import { PaymentsOptionItem } from 'modules/banking/components/AddTransfersToBulkPaymentForm'
import { ConfirmDeleteTransfersModal } from 'modules/banking/components/ConfirmDeleteTransfersModal'
import { PayTransfersInBulkModal } from 'modules/banking/components/PayTransfersInBulkModal'
import { TransfersDataTable } from 'modules/banking/components/TransfersDataTable'
import { useAddTransfersMutation } from 'modules/banking/hooks/useAddTransfers'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

const TransfersPage = () => {
  const {
    access: { locale },
    company,
  } = useCurrentSession()
  const currency = company.legalEntities.find((el) => el.main)?.currency ?? 'EUR'
  const { paymentId: selectedPaymentId } = useSearchParams<{
    paymentId?: string
  }>()
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [addToPaymentModalOpen, toggleAddToPaymentModal] = useToggle(false)
  const [bulkDeleteConfirmModalOpen, toggleBulkDeleteConfirmModal] = useToggle(false)
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { data, loading, error } = useQuery(TransfersPageDocument, {
    variables: {
      onlyInitPayments: true,
    },
  })

  const canSeePaymentsModule =
    !!data?.finance?._policies?.sidebarSeeFinancePaymentsSubLevelItem?.can

  const paymentsOptions: PaymentsOptionItem[] =
    data?.banking.payments.flatMap((payment) => {
      return { label: payment.label, value: payment.id }
    }) || []

  const legalEntitiesOptions =
    data?.companies.legalEntities.flatMap((legalEntity) => {
      return { label: legalEntity.legalName, value: legalEntity.id }
    }) || []

  const requestersOptions =
    data?.banking.transfers
      .flatMap((transfer) => {
        return transfer.requester
          ? [{ label: transfer.requester.access.fullName, value: transfer.requester.id }]
          : []
      })
      .filter((option, index, self) => index === self.findIndex((o) => o.value === option.value)) ||
    []

  const { submitAddTransfers, loading: loadingAddition } = useAddTransfersMutation()

  const goToPaymentDetail = (paymentId?: number) => {
    if (paymentId) {
      navigate(
        navigation.finance.banking.payments.paymentId.fullPath({
          id: String(paymentId),
        })
      )
    } else {
      navigate(navigation.finance.banking.payments.fullPath())
    }
  }

  const handleSubmitAddToBulkPayment = async () => {
    await submitAddTransfers({
      transferIds: selectedIds,
      paymentId: Number(selectedPaymentId),
      onSuccess: goToPaymentDetail,
    })
  }

  return (
    <ResponsivePage.Body
      error={error}
      loading={loading}
      authorized={canSeePaymentsModule}
      variant="full"
    >
      <Box flexDirection="column" gap="s32">
        <Box overflowX="hidden">
          <TransfersDataTable
            legalEntitiesOptions={legalEntitiesOptions}
            requestersOptions={requestersOptions}
            onSelectedIds={(ids) => {
              const event = selectedPaymentId
                ? trackingEvents.accounting.payments.selectPayments
                : trackingEvents.accounting.payments.selectOutstandingPayments

              track(event, { payment_count: ids.length })
              setSelectedIds(ids)
            }}
            handleSubmitAddToBulkPayment={
              !!selectedPaymentId ? () => handleSubmitAddToBulkPayment() : undefined
            }
            disabledAddToBulkPayment={!selectedIds.length || loadingAddition}
            toggleAddToPaymentModal={!selectedPaymentId ? toggleAddToPaymentModal(true) : undefined}
            disabledAddToPaymentModal={!selectedIds.length}
            disabledBulkDelete={!selectedIds.length}
            toggleBulkDeleteConfirmModal={
              !selectedPaymentId ? toggleBulkDeleteConfirmModal(true) : undefined
            }
            currency={currency}
            locale={locale}
          />
        </Box>
        {addToPaymentModalOpen && (
          <PayTransfersInBulkModal
            onSuccess={goToPaymentDetail}
            onClose={() => toggleAddToPaymentModal(false)()}
            transferIds={selectedIds}
            paymentsOptions={paymentsOptions}
          />
        )}
        {bulkDeleteConfirmModalOpen && (
          <ConfirmDeleteTransfersModal
            transfersIds={selectedIds}
            onClose={() => toggleBulkDeleteConfirmModal(false)()}
          />
        )}
      </Box>
    </ResponsivePage.Body>
  )
}
export default TransfersPage
